import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  FaBaby,
  FaBalanceScale,
  FaBrain,
  FaGripfire,
  FaRunning,
  FaUser,
  FaUserMd,
  FaVenus,
  FaWeight,
} from "react-icons/fa";
import { LuDna } from "react-icons/lu";
import { FaFaceSmile, FaHandDots } from "react-icons/fa6";

const causes = [
  {
    icon: FaBalanceScale,
    title: "Hormonal Imbalance",
    position: "top-[-190px] left-[-20px] md:top-[-200px] md:left-[10%]",
  },
  {
    icon: LuDna,
    title: "Genetics",
    position: "top-[-190px] left-[76%] md:top-[-200px] md:left-[65%]",
  },
  {
    icon: FaWeight,
    title: "Insulin Resistance",
    position: "top-[-50px] left-[-20px] md:top-[-20px] md:left-[10%]",
  },
  {
    icon: FaGripfire,
    title: "Gut Inflammation",
    position: "top-[-50px] left-[67%] md:top-[-20px] md:left-[62%]",
  },
  {
    icon: FaRunning,
    title: "Lifestyle Factors",
    position: "top-[30px] left-[22%] md:top-[140px] md:left-[39%]",
  },
];

const symptoms = [
  {
    text: "Irregular or absent periods",
    icon: <FaVenus className="text-pink-600 text-lg" />,
  },
  {
    text: "Excessive hair growth (hirsutism)",
    icon: <FaUserMd className="text-pink-600 text-lg" />,
  },
  {
    text: "Acne and oily skin",
    icon: <FaFaceSmile className="text-pink-600 text-lg" />,
  },
  {
    text: "Weight gain, especially around the abdomen",
    icon: <FaWeight className="text-pink-600 text-lg" />,
  },
  {
    text: "Thinning hair or male-pattern baldness",
    icon: <FaUser className="text-pink-600 text-lg" />,
  },
  {
    text: "Darkened skin patches (acanthosis nigricans)",
    icon: <FaHandDots className="text-pink-600 text-lg" />,
  },
  {
    text: "Infertility or difficulty in conceiving",
    icon: <FaBaby className="text-pink-600 text-lg" />,
  },
  {
    text: "Mood swings, anxiety, or depression",
    icon: <FaBrain className="text-pink-600 text-lg" />,
  },
];

const ScreenThree = () => {
  function useScreenWidth() {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
      const handleResize = () => setWidth(window.innerWidth);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    return width;
  }
  const width = useScreenWidth();

  return (
    <section id="pcod" className="relative px-2 md:px-10 h-full md:py-12 overflow-x-hidden">
      <div className="mx-auto flex flex-col md:flex-row items-center justify-center gap-10">
        <div className="flex-1 overflow-x-hidden">
          <h2 className="text-3xl md:text-4xl font-bold text-pink-700 mb-6">
            Common Symptoms of PCOD
          </h2>
          <motion.ul className="text-gray-700 space-y-4">
            {symptoms.map((symptom, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -100 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, amount: 0.5 }}
                transition={{
                  type: "spring",
                  stiffness: 120,
                  delay: index * 0.2,
                }}
                className="flex items-center gap-3"
              >
                {symptom.icon}
                <span>{symptom.text}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center relative gap-4">
          <h3 className="text-3xl font-semibold text-pink-600 mb-6 text-center">
            Causes of PCOD
          </h3>

          <div className="relative w-60 h-60 md:w-80 md:h-80 flex items-center justify-center">
            <motion.img
              src="ovary.png"
              alt="PCOD Ovary"
              className="absolute w-36 h-36 md:w-48 md:h-48"
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", duration: 0.8 }}
            />

            {causes.map((cause, index) => {
              const Icon = cause.icon;
              const angle = (index / causes.length) * 360;

              let radius = 90;
              if (window.innerWidth >= 768) radius = 130;
              if (window.innerWidth >= 1024) radius = 160;

              const x = radius * Math.cos((angle * Math.PI) / 180);
              const y = radius * Math.sin((angle * Math.PI) / 180);

              return (
                <motion.div
                  key={index}
                  className="absolute flex flex-col items-center gap-1 w-20 text-center group"
                  style={{
                    left: `calc(35% + ${x}px)`,
                    top: `calc(38% + ${y}px)`,
                    transform: "translate(-50%, -50%)",
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 120,
                    delay: index * 0.2,
                  }}
                >
                  <Icon className="text-[#020046] text-xl md:text-3xl group-hover:scale-120 cursor-pointer transition duration-300" />
                  <h4 className="font-bold text-[11px] md:text-sm">
                    {cause.title}
                  </h4>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ScreenThree;
